import React, { createContext, useContext, useState, useEffect } from 'react';
import { decodeJwt, setAuthToken } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is authenticated on app load
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const userRole = localStorage.getItem('userRole');

      console.log('Auth check - accessToken:', !!accessToken, 'userRole:', userRole);
      console.log('Auth check - full accessToken:', accessToken);

      if (!accessToken || !userRole) {
        console.log('Auth check - missing token or role, setting user to null');
        setUser(null);
        setLoading(false);
        return;
      }

      // Decode and validate token
      const decodedToken = decodeJwt(accessToken);
      const currentTime = Math.floor(Date.now() / 1000);

      console.log('Auth check - decodedToken:', decodedToken);
      console.log('Auth check - token exp:', decodedToken.exp, 'current time:', currentTime);
      console.log('Auth check - token expired:', decodedToken.exp < currentTime);

      if (decodedToken.exp < currentTime) {
        // Token expired
        console.log('Auth check - token expired, logging out');
        logout();
        return;
      }

      // Map the token role to the client role for comparison
      const tokenRole = decodedToken.role === 'user' ? 'student' : decodedToken.role;
      
      // Check if user role matches token role (with mapping)
      if (tokenRole !== userRole) {
        console.log('Role mismatch:', { tokenRole, userRole, decodedTokenRole: decodedToken.role });
        logout();
        return;
      }

      // User is authenticated
      console.log('Auth check - user authenticated successfully');
      console.log('Auth check - setting user with role:', userRole, 'original JWT role:', decodedToken.role);
      console.log('Auth check - decodedToken keys:', Object.keys(decodedToken));
      console.log('Auth check - decodedToken._id:', decodedToken._id);
      console.log('Auth check - decodedToken.id:', decodedToken.id);
      console.log('Auth check - decodedToken.userId:', decodedToken.userId);
      
      const userId = decodedToken._id || decodedToken.id || decodedToken.userId;
      console.log('Auth check - extracted userId:', userId);
      
      setUser({
        ...decodedToken,
        role: userRole, // Use the client role (student) - override the JWT role
        accessToken,
        id: userId, // Extract user ID from token
      });
      // Set the Authorization header for API calls
      setAuthToken(accessToken);
    } catch (error) {
      console.error('Auth check failed:', error);
      // In production, if token decoding fails, clear everything and start fresh
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (userData) => {
    try {
      // Decode the JWT token to extract user ID
      const decodedToken = decodeJwt(userData.accessToken);
      console.log('Login - decodedToken:', decodedToken);
      console.log('Login - decodedToken keys:', Object.keys(decodedToken));
      console.log('Login - decodedToken._id:', decodedToken._id);
      console.log('Login - decodedToken.id:', decodedToken.id);
      console.log('Login - decodedToken.userId:', decodedToken.userId);
      
      const userId = decodedToken._id || decodedToken.id || decodedToken.userId;
      console.log('Login - extracted userId:', userId);
      
      // Create user object with extracted ID
      const userWithId = {
        ...userData,
        ...decodedToken,
        role: userData.role, // Use the provided role
        id: userId, // Extract user ID from token
      };
      
      console.log('Login - final user object:', userWithId);
      
      setUser(userWithId);
      localStorage.setItem('accessToken', userData.accessToken);
      localStorage.setItem('userRole', userData.role);
      // Set the Authorization header for API calls
      setAuthToken(userData.accessToken);
    } catch (error) {
      console.error('Login - JWT decode failed:', error);
      // Fallback to original userData if JWT decoding fails
      setUser(userData);
      localStorage.setItem('accessToken', userData.accessToken);
      localStorage.setItem('userRole', userData.role);
      setAuthToken(userData.accessToken);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('userRole');
    // Clear any other user-related data
    localStorage.removeItem('userProfile');
    localStorage.removeItem('userPreferences');
    // Clear Authorization header
    setAuthToken(null);
  };

  const isAuthenticated = () => {
    return user !== null && user.role && !!user.accessToken;
  };

  const hasRole = (role) => {
    return user && user.role && user.role === role;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated,
    hasRole,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
